/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import React, { useCallback, useMemo, useRef, useState } from 'react';
import { ChevronDown, Image, Tag, X, Palette, Package, ImageIcon, User, Layers, Brush, Layout } from 'lucide-react';

export type ImageCategory = {
  value: string;
  label: string;
  icon?: React.ReactNode;
};

// Default category icons mapping
const CATEGORY_ICONS: Record<string, React.ReactNode> = {
  reference: <ImageIcon className="w-3 h-3" />,
  style: <Palette className="w-3 h-3" />,
  product: <Package className="w-3 h-3" />,
  background: <Layers className="w-3 h-3" />,
  character: <User className="w-3 h-3" />,
  texture: <Brush className="w-3 h-3" />,
  template: <Layout className="w-3 h-3" />,
};

// Category badge colors
const CATEGORY_COLORS: Record<string, string> = {
  reference: 'bg-blue-500/80',
  style: 'bg-purple-500/80',
  product: 'bg-green-500/80',
  background: 'bg-amber-500/80',
  character: 'bg-pink-500/80',
  texture: 'bg-cyan-500/80',
  template: 'bg-teal-500/80',
};

export interface SecondaryPhotosPanelProps {
  images: SecondaryImage[];
  onUpload: (files: File[], category?: string) => Promise<void> | void;     // purely front-end
  onRemove: (img: SecondaryImage) => Promise<void> | void;
  onCategoryChange?: (img: SecondaryImage, newCategory: string) => void;  // Optional callback when category changes
  categories?: ImageCategory[];                          // Optional list of categories
  defaultCategory?: string;                              // Default category for new uploads
  accept?: string[];                                     // MIME types
  maxSizeMB?: number;
  maxFiles?: number;
  title?: string;
  subtitle?: string;
  cta?: string;
  disabled?: boolean;
}

const defaultAccept = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];

const SecondaryPhotosPanel: React.FC<SecondaryPhotosPanelProps> = ({
  images,
  onUpload,
  onRemove,
  onCategoryChange,
  categories,
  defaultCategory,
  accept = defaultAccept,
  maxSizeMB = 5,
  maxFiles = 3,
  title = 'Additional gallery',
  subtitle = 'Optional images to complement your main one.',
  cta = 'Add photos',
  disabled = false,
}) => {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [dragOver, setDragOver] = useState(false);
  const [errors, setErrors] = useState<string[]>([]);
  const [isExpanded, setIsExpanded] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState<string>(defaultCategory || categories?.[0]?.value || '');

  const acceptSet = useMemo(() => new Set(accept), [accept]);
  const remainingSlots = Math.max(0, maxFiles - (images?.length ?? 0));

  const validateFiles = useCallback((fileList: FileList | null) => {
    const problems: string[] = [];
    const ok: File[] = [];
    if (!fileList) return { ok, problems };

    const list = Array.from(fileList);
    if (list.length > remainingSlots) {
      problems.push(`You can add up to ${remainingSlots} more image${remainingSlots === 1 ? '' : 's'}.`);
    }

    for (const f of list.slice(0, remainingSlots)) {
      if (!acceptSet.has(f.type)) {
        problems.push(`${f.name} has an unsupported type (${f.type || 'unknown'}).`);
        continue;
      }
      if (f.size > maxSizeMB * 1024 * 1024) {
        problems.push(`${f.name} is larger than ${maxSizeMB}MB.`);
        continue;
      }
      ok.push(f);
    }
    return { ok, problems };
  }, [acceptSet, maxSizeMB, remainingSlots]);

  const handleFiles = useCallback(async (fileList: FileList | null) => {
    if (!fileList || disabled) return;
    const { ok, problems } = validateFiles(fileList);
    setErrors(problems);
    if (ok.length === 0) return;
    try {
      // Pass category only if categories are enabled
      await onUpload(ok, categories ? selectedCategory : undefined);
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Upload failed.';
      setErrors((prev) => [...prev, msg]);
    }
  }, [onUpload, validateFiles, disabled, categories, selectedCategory]);

  const openFileDialog = () => { if (!disabled) inputRef.current?.click(); };
  const computedDisabled = disabled || remainingSlots === 0;

  const getCategoryLabel = (category: string) => {
    return categories?.find((cat) => cat.value === category)?.label || '';
  };

  const expandedDiv = (
    <section className="w-full bg-gray-800/50 border border-gray-700 rounded-lg p-4 flex flex-col gap-4 animate-fade-in backdrop-blur-sm">
      {/* Category selector - only shown if categories are provided */}
      {categories && categories.length > 0 && (
        <div className="flex items-center gap-3">
          <Tag className="w-4 h-4 text-gray-400" />
          <label className="text-sm text-gray-400">Category for new uploads:</label>
          <select
            value={selectedCategory}
            onChange={(e) => setSelectedCategory(e.target.value)}
            disabled={computedDisabled}
            className="bg-gray-700 border border-gray-600 text-gray-200 text-sm rounded-md px-3 py-1.5 focus:ring-2 focus:ring-blue-500/60 focus:border-transparent outline-none"
          >
            {categories.map((cat) => (
              <option key={cat.value} value={cat.value}>
                {cat.label}
              </option>
            ))}
          </select>
        </div>
      )}

      {/* Dropzone */}
      <div
        onDragOver={(e) => { e.preventDefault(); if (!computedDisabled) setDragOver(true); }}
        onDragLeave={(e) => { e.preventDefault(); setDragOver(false); }}
        onDrop={(e) => {
          e.preventDefault();
          setDragOver(false);
          if (!computedDisabled) handleFiles(e.dataTransfer?.files ?? null);
        }}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') openFileDialog(); }}
        onClick={openFileDialog}
        aria-label="Drop images here or browse"
        className={`min-h-[110px] rounded-lg border-2 border-dashed p-4 text-center transition-all outline-none focus:ring-2 focus:ring-blue-500/60 ${
          dragOver ? 'border-gray-500 bg-gray-800/60' : 'border-gray-700 hover:bg-gray-800/40'
        } ${computedDisabled ? 'opacity-50' : ''}`}
      >
        <div className="pointer-events-none">
          <Image className="w-8 h-8 mx-auto mb-2 text-gray-500 group-hover:text-gray-400 transition" />
          <p className="text-sm font-medium text-gray-300 mb-1">{title} ({images.length} / {maxFiles})</p>
          <p className="text-xs text-gray-500 mb-3">{subtitle}</p>
          <button className="text-xs font-medium text-blue-400 hover:text-blue-300 transition">
            {cta}
          </button>
        </div>
        <input
          ref={inputRef}
          type="file"
          accept={accept.join(',')}
          multiple
          className="hidden"
          disabled={computedDisabled}
          onChange={(e) => handleFiles(e.target.files)}
        />
      </div>

      {/* Errors */}
      {errors.length > 0 && (
        <ul className="-mt-2 list-inside list-disc text-sm text-red-400">
          {errors.map((err, i) => <li key={i}>{err}</li>)}
        </ul>
      )}

      {/* Thumbnails */}
      {images?.length ? (
        <ul className="grid grid-cols-2 gap-3 md:grid-cols-3 lg:grid-cols-4">
          {images.map((img, idx) => (
            <li key={img.id ?? img.url ?? idx} className="group relative overflow-hidden rounded-md border border-gray-700 bg-gray-800">
              <img
                src={img.url}
                alt={img.alt || `Secondary photo ${idx + 1}`}
                className="h-32 w-full object-cover"
                loading="lazy"
              />
              
              {/* Top bar with category badge and remove button */}
              <div className="absolute top-0 left-0 right-0 flex items-center justify-between p-2">
                {/* Category badge with icon only - only shown if categories are enabled and image has a category */}
                {categories && img.category ? (
                  <span 
                    className={`flex items-center justify-center w-6 h-6 text-white rounded-full backdrop-blur-sm shadow-sm ${CATEGORY_COLORS[img.category] || 'bg-gray-500/80'}`}
                    title={getCategoryLabel(img.category)}
                  >
                    {CATEGORY_ICONS[img.category]}
                  </span>
                ) : (
                  <div /> /* Empty div for spacing when no category */
                )}
                
                {/* Remove button */}
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    onRemove(img);
                  }}
                  className="p-1.5 rounded-full bg-red-500/90 hover:bg-red-500 text-white transition-all hover:scale-110 active:scale-95 shadow-lg"
                  aria-label="Remove image"
                >
                  <X className="w-3 h-3" />
                </button>
              </div>
              
              {/* Category selector on hover - only if categories are enabled */}
              {categories && categories.length > 0 && onCategoryChange && (
                <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/80 to-transparent p-2 opacity-0 transition-opacity group-hover:opacity-100">
                  <select
                    value={img.category || ''}
                    onChange={(e) => {
                      e.stopPropagation();
                      onCategoryChange(img, e.target.value);
                    }}
                    onClick={(e) => e.stopPropagation()}
                    className="w-full bg-white/90 text-gray-900 text-xs font-medium rounded-md px-2 py-1.5 outline-none cursor-pointer"
                    aria-label="Change category"
                  >
                    {categories.map((cat) => (
                      <option key={cat.value} value={cat.value}>
                        {cat.label}
                      </option>
                    ))}
                  </select>
                </div>
              )}
            </li>
          ))}
        </ul>
      ) : (
        <p className="text-sm text-gray-400 -mt-1">No photos yet.</p>
      )}
    </section>
  );

  return (
    <div className="bg-gray-800/40 backdrop-blur-xl border border-gray-700/50 rounded-xl overflow-hidden shadow-lg">
      <button
        onClick={() => setIsExpanded(state => !state)}
        className="w-full px-4 py-3 flex items-center justify-between hover:bg-gray-700/20 transition"
      >
        <div className="flex items-center gap-2">
          <Image className="w-4 h-4 text-purple-400" />
          <h3 className="text-sm font-semibold text-gray-200">{title}</h3>
          {images.length > 0 && (
            <span className="text-xs text-white bg-purple-500/80 px-2 py-0.5 rounded-full">{images.length}</span>
          )}
          <span className="text-xs text-gray-500 bg-gray-700/50 px-2 py-0.5 rounded-full">Optional</span>
        </div>
        <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
      </button>
      
      {/* Use CSS to hide instead of unmounting to preserve blob URLs */}
      <div className={`px-4 pb-4 pt-3 ${isExpanded ? 'block' : 'hidden'}`}>
        {expandedDiv}
      </div>
    </div>
  )
};

export default SecondaryPhotosPanel;
